<script setup>
/**
 * Field report G2.1: when the assistant may START a message (a follow-up, a broadcast). A reply to
 * a customer who just wrote always goes at once, and the section says so.
 *
 * The value is the BFF's `sendingHours` exactly (`config-bag.ts` `SendingHoursDTO`): `week` is
 * `null` for "Your opening hours", the Friday prayer pause, the seasons (Ramadan) and the days off
 * (Eid) with dates she enters, and whether broadcasts wait too. This control offers only what the
 * server accepts (at most six seasons and twelve days off) and shows the sentence a save comes back
 * with.
 */
import { computed } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';

import HoursEditor from './HoursEditor.vue';
import WasloInput from './WasloInput.vue';
import { useWasloI18n } from '../i18n.js';
import {
  MAX_PAUSES,
  MAX_SEASONS,
  MAX_SENDING_NAME_CHARS,
  defaultSendingHours,
} from '../helpers/sending-hours.js';

const props = defineProps({
  /** `SendingHoursDTO`. */
  modelValue: { type: Object, default: null },
  /** The opening hours, to start a week of her own from. `null`: none set. */
  businessHours: { type: Object, default: null },
  /** The save's sentence for this section, if it refused one. */
  message: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useWasloI18n();

const value = computed(() => props.modelValue ?? defaultSendingHours());
const write = changes =>
  emit('update:modelValue', { ...value.value, ...changes });

const WEEKDAYS = ['sun', 'mon', 'tue', 'wed', 'thu'];
/** A week to start from: her opening hours, else Sunday to Thursday, nine to five. */
const startingWeek = () =>
  props.businessHours
    ? JSON.parse(JSON.stringify(props.businessHours))
    : Object.fromEntries(WEEKDAYS.map(day => [day, [['09:00', '17:00']]]));

// Ids carry a prefix because `RadioCard` writes its id onto a real `<input>`.
const weekOptions = computed(() => [
  {
    id: 'week_opening',
    label: t('WASLO.SENDING_HOURS.WEEK.OPENING'),
    description: props.businessHours
      ? t('WASLO.SENDING_HOURS.WEEK.OPENING_HINT')
      : t('WASLO.SENDING_HOURS.WEEK.OPENING_NONE_HINT'),
  },
  {
    id: 'week_own',
    label: t('WASLO.SENDING_HOURS.WEEK.OWN'),
    description: t('WASLO.SENDING_HOURS.WEEK.OWN_HINT'),
  },
]);
const weekChoice = computed(() =>
  value.value.week === null ? 'week_opening' : 'week_own'
);
const selectWeek = id => {
  if (id === 'week_opening') write({ week: null });
  if (id === 'week_own' && value.value.week === null) {
    write({ week: startingWeek() });
  }
};

const setGap = changes =>
  write({ prayerGap: { ...value.value.prayerGap, ...changes } });

const addSeason = () => {
  if (value.value.seasons.length >= MAX_SEASONS) return;
  write({
    seasons: [
      ...value.value.seasons,
      { name: '', from: '', to: '', week: startingWeek() },
    ],
  });
};
const setSeason = (index, changes) =>
  write({
    seasons: value.value.seasons.map((season, i) =>
      i === index ? { ...season, ...changes } : season
    ),
  });
const removeSeason = index =>
  write({ seasons: value.value.seasons.filter((_, i) => i !== index) });

const addPause = () => {
  if (value.value.pauses.length >= MAX_PAUSES) return;
  write({ pauses: [...value.value.pauses, { name: '', from: '', to: '' }] });
};
const setPause = (index, changes) =>
  write({
    pauses: value.value.pauses.map((pause, i) =>
      i === index ? { ...pause, ...changes } : pause
    ),
  });
const removePause = index =>
  write({ pauses: value.value.pauses.filter((_, i) => i !== index) });
</script>

<template>
  <div class="flex flex-col gap-4" data-waslo-sending-hours>
    <SettingsHeader
      :heading="t('WASLO.SENDING_HOURS.HEADING')"
      :description="t('WASLO.SENDING_HOURS.DESCRIPTION')"
    />

    <div class="flex flex-col gap-3">
      <RadioCard
        v-for="option in weekOptions"
        :id="option.id"
        :key="option.id"
        :label="option.label"
        :description="option.description"
        :is-active="weekChoice === option.id"
        :data-waslo-sending-week="option.id"
        @select="selectWeek"
      />
    </div>
    <HoursEditor
      v-if="value.week !== null"
      :model-value="value.week"
      :heading="t('WASLO.SENDING_HOURS.WEEK.HEADING')"
      :description="t('WASLO.SENDING_HOURS.WEEK.OWN_HINT')"
      :with-timezone="false"
      data-waslo-sending-own-week
      @update:model-value="week => write({ week: week ?? startingWeek() })"
    />

    <div class="flex flex-col gap-2" data-waslo-sending-prayer>
      <label class="inline-flex items-center gap-2 text-sm text-n-slate-12">
        <Checkbox
          :model-value="value.prayerGap.on"
          data-waslo-sending-prayer-on
          @update:model-value="on => setGap({ on })"
        />
        {{ t('WASLO.SENDING_HOURS.PRAYER.LABEL') }}
      </label>
      <div v-if="value.prayerGap.on" class="flex flex-wrap items-end gap-2">
        <WasloInput
          type="time"
          :model-value="value.prayerGap.from"
          :label="t('WASLO.SENDING_HOURS.PRAYER.FROM')"
          data-waslo-sending-prayer-from
          @update:model-value="from => setGap({ from })"
        />
        <WasloInput
          type="time"
          :model-value="value.prayerGap.to"
          :label="t('WASLO.SENDING_HOURS.PRAYER.TO')"
          data-waslo-sending-prayer-to
          @update:model-value="to => setGap({ to })"
        />
      </div>
    </div>

    <div class="flex flex-col gap-3" data-waslo-sending-seasons>
      <h3 class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t('WASLO.SENDING_HOURS.SEASONS.HEADING') }}
      </h3>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('WASLO.SENDING_HOURS.SEASONS.DESCRIPTION') }}
      </p>
      <div
        v-for="(season, index) in value.seasons"
        :key="index"
        class="flex flex-col gap-3 p-4 outline outline-1 rounded-xl outline-n-weak"
        data-waslo-sending-season
      >
        <div class="flex flex-wrap items-end gap-2">
          <WasloInput
            :model-value="season.name"
            :label="t('WASLO.SENDING_HOURS.SEASONS.NAME')"
            :placeholder="t('WASLO.SENDING_HOURS.SEASONS.NAME_PLACEHOLDER')"
            :max-length="MAX_SENDING_NAME_CHARS"
            @update:model-value="name => setSeason(index, { name })"
          />
          <WasloInput
            type="date"
            :model-value="season.from"
            :label="t('WASLO.SENDING_HOURS.SEASONS.FROM')"
            @update:model-value="from => setSeason(index, { from })"
          />
          <WasloInput
            type="date"
            :model-value="season.to"
            :label="t('WASLO.SENDING_HOURS.SEASONS.TO')"
            @update:model-value="to => setSeason(index, { to })"
          />
          <Button
            size="xs"
            variant="ghost"
            color="slate"
            :label="t('WASLO.SENDING_HOURS.SEASONS.REMOVE')"
            data-waslo-sending-season-remove
            @click="removeSeason(index)"
          />
        </div>
        <HoursEditor
          :model-value="season.week"
          :heading="t('WASLO.SENDING_HOURS.SEASONS.WEEK')"
          :description="t('WASLO.SENDING_HOURS.SEASONS.WEEK_HINT')"
          :with-timezone="false"
          @update:model-value="
            week => setSeason(index, { week: week ?? startingWeek() })
          "
        />
      </div>
      <Button
        v-if="value.seasons.length < MAX_SEASONS"
        class="self-start"
        size="xs"
        variant="faded"
        color="slate"
        :label="t('WASLO.SENDING_HOURS.SEASONS.ADD')"
        data-waslo-sending-season-add
        @click="addSeason"
      />
    </div>

    <div class="flex flex-col gap-3" data-waslo-sending-pauses>
      <h3 class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t('WASLO.SENDING_HOURS.PAUSES.HEADING') }}
      </h3>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('WASLO.SENDING_HOURS.PAUSES.DESCRIPTION') }}
      </p>
      <div
        v-for="(pause, index) in value.pauses"
        :key="index"
        class="flex flex-wrap items-end gap-2"
        data-waslo-sending-pause
      >
        <WasloInput
          :model-value="pause.name"
          :label="t('WASLO.SENDING_HOURS.PAUSES.NAME')"
          :placeholder="t('WASLO.SENDING_HOURS.PAUSES.NAME_PLACEHOLDER')"
          :max-length="MAX_SENDING_NAME_CHARS"
          @update:model-value="name => setPause(index, { name })"
        />
        <WasloInput
          type="date"
          :model-value="pause.from"
          :label="t('WASLO.SENDING_HOURS.PAUSES.FROM')"
          @update:model-value="from => setPause(index, { from })"
        />
        <WasloInput
          type="date"
          :model-value="pause.to"
          :label="t('WASLO.SENDING_HOURS.PAUSES.TO')"
          @update:model-value="to => setPause(index, { to })"
        />
        <Button
          size="xs"
          variant="ghost"
          color="slate"
          :label="t('WASLO.SENDING_HOURS.PAUSES.REMOVE')"
          data-waslo-sending-pause-remove
          @click="removePause(index)"
        />
      </div>
      <Button
        v-if="value.pauses.length < MAX_PAUSES"
        class="self-start"
        size="xs"
        variant="faded"
        color="slate"
        :label="t('WASLO.SENDING_HOURS.PAUSES.ADD')"
        data-waslo-sending-pause-add
        @click="addPause"
      />
    </div>

    <div class="flex flex-col gap-1" data-waslo-sending-broadcasts>
      <label class="inline-flex items-center gap-2 text-sm text-n-slate-12">
        <Checkbox
          :model-value="value.broadcasts"
          @update:model-value="broadcasts => write({ broadcasts })"
        />
        {{ t('WASLO.SENDING_HOURS.BROADCASTS') }}
      </label>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('WASLO.SENDING_HOURS.BROADCASTS_HINT') }}
      </p>
    </div>

    <p class="mb-0 text-sm text-n-slate-11" data-waslo-sending-website>
      {{ t('WASLO.SENDING_HOURS.WEBSITE_NOTE') }}
    </p>
    <p
      v-if="message"
      class="mb-0 text-sm text-n-ruby-9"
      data-waslo-sending-hours-error
    >
      {{ message }}
    </p>
  </div>
</template>
